import { formatMoney, parseMoney, type Cents } from '@keel/core';
import {
  createContext, forwardRef, type AriaAttributes, useContext, useEffect, useId, useState,
  type InputHTMLAttributes, type KeyboardEvent, type LabelHTMLAttributes, type ReactNode, type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';
import { useAppStore } from '@/lib/store';
import { currencySymbol, useFormatContext } from '@/lib/money';

// ───────────────────────── Field (label + hint + error wiring) ─────────────────────────
interface FieldCtx { readonly id: string; readonly describedBy: string | undefined; readonly invalid: boolean }
const FieldContext = createContext<FieldCtx | null>(null);
/** For custom controls: returns the id / aria props the surrounding `<Field>` expects. */
export const useFieldControl = (): FieldCtx | null => useContext(FieldContext);

export const Label = ({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) => (
  <label className={cn('text-sm font-medium text-text', className)} {...props} />
);

export interface FieldProps {
  readonly label: ReactNode;
  readonly hint?: ReactNode;
  /** Error message; shown under the control and announced via aria-describedby. */
  readonly error?: string | undefined;
  readonly optional?: boolean;
  readonly className?: string;
  /** Force an id (otherwise generated). */
  readonly id?: string;
  readonly children: ReactNode;
}

/** Visible label, optional hint and inline error for one control. Controls inside pick up id/aria automatically. */
export const Field = ({ label, hint, error, optional, className, id, children }: FieldProps) => {
  const gen = useId();
  const controlId = id ?? `f${gen}`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errId = error ? `${controlId}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={controlId}>
        {label}
        {optional ? <span className="ml-1 font-normal text-text-muted">(optional)</span> : null}
      </Label>
      <FieldContext.Provider value={{ id: controlId, describedBy, invalid: Boolean(error) }}>{children}</FieldContext.Provider>
      {hint ? (
        <p id={hintId} className="text-xs text-text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errId} className="text-xs font-medium text-negative" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};

const controlBase =
  'block w-full rounded-md border border-border-strong/70 bg-surface text-sm text-text placeholder:text-text-muted shadow-sm ' +
  'transition-colors duration-fast focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/40 ' +
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-70 aria-[invalid=true]:border-negative aria-[invalid=true]:focus:ring-negative/30';

const useControlProps = (props: { id?: string; 'aria-describedby'?: string; 'aria-invalid'?: AriaAttributes['aria-invalid'] }) => {
  const f = useFieldControl();
  return {
    id: props.id ?? f?.id,
    'aria-describedby': props['aria-describedby'] ?? f?.describedBy,
    'aria-invalid': props['aria-invalid'] ?? (f?.invalid || undefined),
  };
};

// ───────────────────────── Input / Textarea ─────────────────────────
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Leading adornment (icon or symbol) inside the field. */
  readonly start?: ReactNode;
  /** Trailing adornment. */
  readonly end?: ReactNode;
}

/** Text input, 40px tall (44px on touch). Wrap in `<Field>` for a label. */
export const Input = forwardRef<HTMLInputElement, InputProps>(({ className, start, end, ...props }, ref) => {
  const aria = useControlProps(props);
  const input = (
    <input
      ref={ref}
      className={cn('form-input h-10 px-3 coarse:h-11', controlBase, start ? 'pl-9' : '', end ? 'pr-10' : '', className)}
      {...props}
      {...aria}
    />
  );
  if (!start && !end) return input;
  return (
    <div className="relative">
      {start ? (
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-text-muted [&_svg]:size-4">{start}</span>
      ) : null}
      {input}
      {end ? <span className="absolute inset-y-0 right-2 flex items-center text-text-muted">{end}</span> : null}
    </div>
  );
});
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...props }, ref) => {
  const aria = useControlProps(props);
  return <textarea ref={ref} className={cn('form-textarea min-h-24 px-3 py-2', controlBase, className)} {...props} {...aria} />;
});
Textarea.displayName = 'Textarea';

// ───────────────────────── NumberInput ─────────────────────────
export interface NumberInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  readonly value: number | null;
  readonly onChange: (value: number | null) => void;
  /** Shown after the number, e.g. "%". */
  readonly suffix?: ReactNode;
}

/** Plain number entry (percentages, units). Empty → `null`. */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(({ value, onChange, suffix, className, ...props }, ref) => (
  <Input
    ref={ref}
    type="number"
    inputMode="decimal"
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    end={suffix ? <span className="pr-1 text-sm">{suffix}</span> : undefined}
    className={cn('tabular-nums', className)}
    {...props}
  />
));
NumberInput.displayName = 'NumberInput';

// ───────────────────────── MoneyInput ─────────────────────────
export interface MoneyInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type' | 'start'> {
  /** Integer minor units, or null when empty. */
  readonly value: Cents | null;
  readonly onChange: (cents: Cents | null) => void;
  /** Defaults to the current entity currency. */
  readonly currency?: string;
  /** Allow negative values (enables the `-` key). Default true. */
  readonly allowNegative?: boolean;
  /** Called when the text can't be parsed (on blur). */
  readonly onParseError?: (text: string) => void;
  /** Report the amount only on blur / Enter (for fields that save straight to the server on change). */
  readonly commitOnBlur?: boolean;
}

// ponytail: the editable text always uses '.' decimals (en-AU digits) because core `parseMoney` strips commas;
// add locale-aware parsing when a comma-decimal market ships.
const toText = (c: Cents | null, ccy: string): string => (c === null ? '' : formatMoney(c, ccy, 'en-AU', { hideSymbol: true }));

/**
 * Currency amount entry. Parses with core `parseMoney` as you type, paste or fill ("1,234.5" → 123450) and tidies the text on blur. Keyboard: `-` makes the
 * amount negative, `+` positive, ↑/↓ step by 1 (Shift: 10) major units.
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(
  ({ value, onChange, currency, allowNegative = true, commitOnBlur = false, onParseError, onBlur, onKeyDown, className, ...props }, ref) => {
    const ctx = useFormatContext();
    const ccy = currency ?? ctx.currency;
    const [text, setText] = useState(() => toText(value, ccy));
    const [focused, setFocused] = useState(false);
    const [invalid, setInvalid] = useState(false);
    // privacy blur hides typed amounts too (shown while you edit the field)
    const blur = useAppStore((s) => s.privacyBlur) && !focused;

    useEffect(() => {
      if (!focused) setText(toText(value, ccy));
    }, [value, ccy, focused]);

    const commit = (raw: string) => {
      if (raw.trim() === '') {
        setInvalid(false);
        onChange(null);
        return;
      }
      try {
        let c = parseMoney(raw, ccy);
        if (!allowNegative) c = Math.abs(c);
        setInvalid(false);
        onChange(c);
        setText(toText(c, ccy));
      } catch {
        setInvalid(true);
        onParseError?.(raw);
      }
    };

    const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      const current = (() => {
        try {
          return text.trim() ? parseMoney(text, ccy) : 0;
        } catch {
          return null;
        }
      })();
      if ((e.key === '-' && allowNegative) || e.key === '+') {
        e.preventDefault();
        if (current === null) return;
        const next = e.key === '-' ? -Math.abs(current) : Math.abs(current);
        setText(current === 0 && e.key === '-' ? '-' : toText(next, ccy));
        if (current !== 0) onChange(next);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        if (current === null) return;
        const step = (e.shiftKey ? 10 : 1) * 10 ** (ccy === 'JPY' ? 0 : 2);
        let next = current + (e.key === 'ArrowUp' ? step : -step);
        if (!allowNegative) next = Math.max(0, next);
        setText(toText(next, ccy));
        onChange(next);
      } else if (e.key === 'Enter') {
        commit(e.currentTarget.value);
      }
    };

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={text}
        start={<span aria-hidden>{currencySymbol(ccy, ctx.locale)}</span>}
        onFocus={() => setFocused(true)}
        // typed, pasted or filled programmatically: report the amount as soon as it parses (blur still tidies the text)
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          if (commitOnBlur) return;
          if (raw.trim() === '') return onChange(null);
          try {
            const c = parseMoney(raw, ccy);
            setInvalid(false);
            onChange(allowNegative ? c : Math.abs(c));
          } catch {
            // half-typed ("-", "1,") — judged on blur
          }
        }}
        onBlur={(e) => {
          setFocused(false);
          commit(e.target.value);
          onBlur?.(e);
        }}
        onKeyDown={handleKey}
        className={cn('min-w-[7.5rem] text-right tabular-nums', blur && text && 'privacy-blur-text', className)}
        {...props}
        aria-invalid={invalid || undefined}
      />
    );
  },
);
MoneyInput.displayName = 'MoneyInput';

// ───────────────────────── DateInput ─────────────────────────
/** Native `<input type="date">` (value is an ISODate `YYYY-MM-DD`). */
export const DateInput = forwardRef<HTMLInputElement, Omit<InputProps, 'type'>>(({ className, ...props }, ref) => (
  <Input ref={ref} type="date" className={cn('tabular-nums', className)} {...props} />
));
DateInput.displayName = 'DateInput';
